import {
  OnChange,
  OnChangeWithValidation,
  OnCheckBoxChange,
  OnCurrencyChange,
  OnCurrencyChangeWithValidation,
  OnDateBlur,
  OnDateChange,
  OnDateChangeWithValidation,
} from 'helpers/ValidationHelper';
import { useRef, useMemo, useCallback, useReducer } from 'react';

export const useEditBase = (state: any, validationDataParam: any = {}, setState?: (updater: any) => void) => {
  const validationData = useRef(validationDataParam);
  const stateRef = useRef(state);
  
  // Update the ref on every render to have the latest state
  stateRef.current = state;

  const stateProxy = useMemo(() => {
    return new Proxy({} as any, {
      set: (target: any, prop: string, value: any) => {
        const setterName = `set${prop.charAt(0).toUpperCase()}${prop.slice(1)}`;
        if (stateRef.current[setterName] && typeof stateRef.current[setterName] === 'function') {
          // Individual state properties pattern: state has set{PropName} functions
          stateRef.current[setterName](value);
        } else if (setState) {
          // Single object pattern: use provided setState with functional updater
          setState((prev: any) => ({ ...prev, [prop]: value }));
        }
        return true;
      },
      get: (target: any, prop: string) => {
        return stateRef.current[prop];
      },
      has: (target: any, prop: string) => {
        return prop in stateRef.current;
      },
      ownKeys: () => {
        return Reflect.ownKeys(stateRef.current);
      },
      getOwnPropertyDescriptor: (target: any, prop: string) => {
        if (prop in stateRef.current) {
          return { configurable: true, enumerable: true, writable: true, value: stateRef.current[prop] };
        }
        return undefined;
      },
    });
  }, [setState]);

  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const mockComponent = useMemo(() => ({
    setState: () => forceUpdate(),
    get state() {
      return stateRef.current;
    }
  }), [forceUpdate]);

  const mockComponentRef = useRef(mockComponent);

  const onChangeHandler = useCallback((event: any) => OnChange(mockComponentRef.current, stateProxy, event), []);
  
  const onChangeWithValidationHandler = useCallback((event: any, allowLiteralNull: boolean = false) =>
    OnChangeWithValidation(mockComponentRef.current, stateProxy, validationData.current, event, allowLiteralNull), []);
  
  const onCurrencyChangeHandler = useCallback((event: any) => OnCurrencyChange(mockComponentRef.current, stateProxy, event), []);
  
  const onCurrencyChangeWithValidationHandler = useCallback((event: any) =>
    OnCurrencyChangeWithValidation(mockComponentRef.current, stateProxy, validationData.current, event), []);
  
  const onDateChangeHandler = useCallback((value: any, name: any) => OnDateChange(mockComponentRef.current, stateProxy, value, name), []);
  
  const onDateChangeWithValidationHandler = useCallback((value: any, name: any) =>
    OnDateChangeWithValidation(mockComponentRef.current, stateProxy, validationData.current, value, name), []);
  
  const onDateBlurHandler = useCallback((value: any, name: any) => OnDateBlur(mockComponentRef.current, stateProxy, validationData.current, value, name), []);
  
  const onCheckBoxChangeHandler = useCallback((event: any) => OnCheckBoxChange(mockComponentRef.current, stateProxy, event), []);

  const genericChangeHandler = useCallback((event: any, name?: string, data?: any, allowLiteralNull: boolean = false) => {
    let nameString;
    let value;
    if (data) {
      if (name && name.includes(';')) {
        nameString = name.split(';')[0];
        const ev = {
          target: { name: name.split(';')[1], value: data.selectedText },
        };
        onChangeHandler(ev);
      } else {
        nameString = name;
      }
      value = event;
    } else if (name !== undefined && typeof event === 'object' && 'target' in event && 'checked' in event.target) {
      nameString = event.target.name;
      value = event.target.checked;
    } else if (name) {
      nameString = name;
      value = event;
    } else if ('target' in event) {
      nameString = event.target.name;
      nameString = nameString.includes(';') ? nameString.split(';')[0] : nameString;
      value = event.target.value;
      if (
        'nativeEvent' in event &&
        'target' in event.nativeEvent &&
        'selectedIndex' in event.nativeEvent.target &&
        event.target.name.includes(';')
      ) {
        const index = event.nativeEvent.target.selectedIndex;
        const selectedText = event.nativeEvent.target[index].text;
        const selectedName = event.target.name.split(';')[1];
        const ev = { target: { name: selectedName, value: selectedText } };
        onChangeHandler(ev);
      }
    } else if ('value' in event) {
      nameString = event.name;
      value = event.value;
    }
    let e = { target: { name: nameString, value } };
    if (nameString in validationData.current) {
      onChangeWithValidationHandler(e, allowLiteralNull);
    } else {
      onChangeHandler(e);
    }
  }, [onChangeHandler, onChangeWithValidationHandler]);

  return {
    onChangeHandler,
    onChangeWithValidationHandler,
    onCurrencyChangeHandler,
    onCurrencyChangeWithValidationHandler,
    onDateChangeHandler,
    onDateChangeWithValidationHandler,
    onDateBlurHandler,
    onCheckBoxChangeHandler,
    validationData,
    genericChangeHandler,
    mockComponent: mockComponentRef,
    stateProxy,
  };
};
